import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldCheck, ChevronRight, AlertCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_STYLES = {
  green: {
    text: "text-emerald-300",
    bg: "bg-emerald-500/15",
    border: "border-emerald-500/40",
    ring: "stroke-emerald-400",
    label: "Survey-Ready",
  },
  amber: {
    text: "text-amber-300",
    bg: "bg-amber-500/15",
    border: "border-amber-500/40",
    ring: "stroke-amber-400",
    label: "At Risk",
  },
  red: {
    text: "text-red-300",
    bg: "bg-red-500/15",
    border: "border-red-500/40",
    ring: "stroke-red-400",
    label: "Action Required",
  },
};

/** Animated SVG ring filled by score (0-100). Pure CSS arc using stroke-dasharray. */
function ReadinessRing({ score, band }) {
  const styles = BAND_STYLES[band] || BAND_STYLES.amber;
  const RADIUS = 50;
  const C = 2 * Math.PI * RADIUS;
  const offset = C * (1 - score / 100);
  return (
    <div className="relative w-[140px] h-[140px] flex items-center justify-center" data-testid="readiness-ring">
      <svg width="140" height="140" viewBox="0 0 140 140" className="transform -rotate-90">
        <circle cx="70" cy="70" r={RADIUS} className="fill-none stroke-zinc-800" strokeWidth="10" />
        <circle
          cx="70" cy="70" r={RADIUS}
          className={`fill-none ${styles.ring} transition-all duration-1000 ease-out`}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${styles.text}`}>{score}</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Readiness</span>
      </div>
    </div>
  );
}

function DimensionRow({ dim, expanded, onToggle, onMarkReviewed }) {
  const styles = BAND_STYLES[dim.band] || BAND_STYLES.amber;
  const failingCount = (dim.failing_items || []).length;
  return (
    <div
      className={`rounded-lg border ${styles.border} ${styles.bg} transition-all`}
      data-testid={`readiness-dim-${dim.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`flex-shrink-0 w-1 self-stretch rounded-full ${styles.bg.replace('/15', '/60')}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm truncate">{dim.label}</span>
              <Badge variant="outline" className={`${styles.text} ${styles.border} text-[10px] h-5`}>
                {dim.score}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground truncate">{dim.summary}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {failingCount > 0 && (
            <Badge className="bg-red-500/20 text-red-300 border-red-500/40 text-[10px] h-5">
              {failingCount}
            </Badge>
          )}
          <ChevronRight
            className={`w-4 h-4 transition-transform text-muted-foreground ${expanded ? 'rotate-90' : ''}`}
          />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-white/5 p-3 space-y-2">
          {dim.fix_action && (
            <div className="text-xs text-muted-foreground italic">→ {dim.fix_action}</div>
          )}
          {failingCount === 0 ? (
            <div className="text-xs flex items-center gap-1.5 text-emerald-400">
              <CheckCircle2 className="w-3 h-3" /> Nothing to action.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="readiness-failing-list">
              {dim.failing_items.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start justify-between gap-2 px-2 py-1.5 rounded bg-zinc-900/60 border border-zinc-800 text-xs"
                  data-testid="readiness-failing-item"
                >
                  <div className="flex-1 min-w-0">
                    {/* Render based on dimension shape */}
                    {item.unit && item.last_rounded !== undefined && (
                      <>
                        <span className="font-semibold">{item.unit}</span>
                        <span className="text-muted-foreground ml-1">
                          · {item.last_rounded === 'never' ? 'never rounded' : `${item.days_overdue}d overdue`}
                        </span>
                      </>
                    )}
                    {item.incident_id && (
                      <>
                        <span className="font-semibold">{item.title}</span>
                        {item.unit && <span className="text-muted-foreground ml-1">· {item.unit}</span>}
                        {item.hours_old !== undefined && (
                          <span className="text-amber-400 ml-1">· {Math.round(item.hours_old)}h old</span>
                        )}
                        {item.missing && item.missing.length > 0 && (
                          <span className="text-red-400 ml-1">· missing {item.missing.join(', ')}</span>
                        )}
                      </>
                    )}
                    {item.item_id && item.task_text && (
                      <>
                        <span className="font-semibold">{item.area_name || 'Sprint'}</span>
                        <span className="text-muted-foreground ml-1">: {item.task_text}</span>
                      </>
                    )}
                    {item.assignment_id && (
                      <>
                        <span className="font-semibold">{item.material_title}</span>
                        {item.due_date && <span className="text-red-400 ml-1">· due {item.due_date}</span>}
                      </>
                    )}
                    {item.date && !item.unit && !item.incident_id && (
                      <span className="text-muted-foreground">No flight plan on {item.date}</span>
                    )}
                  </div>
                  {item.incident_id && onMarkReviewed && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); onMarkReviewed(item.incident_id); }}
                      className="h-6 px-2 text-[10px]"
                      data-testid="readiness-mark-reviewed"
                    >
                      Mark reviewed
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReadinessDial() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/readiness/score`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error("readiness load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markReviewed = useCallback(async (incidentId) => {
    try {
      await axios.patch(
        `${API}/incidents/${incidentId}`,
        { reviewed: true },
        { withCredentials: true }
      );
      // Refresh the dial so the score recomputes
      await load();
    } catch (err) {
      console.error("mark reviewed failed", err);
    }
  }, [load]);

  if (loading) {
    return (
      <Card className="bento-card flex items-center justify-center min-h-[180px]" data-testid="readiness-dial-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </Card>
    );
  }
  if (!data) return null;

  const styles = BAND_STYLES[data.band] || BAND_STYLES.amber;
  const dimEntries = Object.entries(data.dimensions);
  // Sort dimensions worst-first so failing items surface
  dimEntries.sort(([, a], [, b]) => a.score - b.score);

  return (
    <Card className={`bento-card ${styles.border}`} data-testid="readiness-dial">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Survey-Readiness Dial
          </span>
          <Badge className={`${styles.bg} ${styles.text} ${styles.border}`}>
            {styles.label}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Live JC/CMS readiness — what would a surveyor see right now.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-5">
          <ReadinessRing score={data.overall_score} band={data.band} />
          <div className="flex-1 space-y-1.5">
            {dimEntries.slice(0, 3).map(([key, dim]) => {
              const s = BAND_STYLES[dim.band] || BAND_STYLES.amber;
              return (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${s.bg.replace('/15', '/80')}`} />
                  <span className="flex-1 truncate text-muted-foreground">{dim.label}</span>
                  <span className={`font-semibold ${s.text}`}>{dim.score}</span>
                </div>
              );
            })}
            {data.band !== "green" && (
              <div className="text-[11px] text-amber-400 flex items-center gap-1 pt-1">
                <AlertCircle className="w-3 h-3" />
                Click below to see what's failing.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-1.5" data-testid="readiness-dimensions">
          {dimEntries.map(([key, dim]) => (
            <DimensionRow
              key={key}
              dim={dim}
              expanded={expanded === key}
              onToggle={() => setExpanded(expanded === key ? null : key)}
              onMarkReviewed={key === "incidents" ? markReviewed : undefined}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
